export interface Code {
  actions: CodeActions[]; // actions that can be performed on the code
  thread_uuid: string;
  code: string;
  start: string;
  end: string;
  name: string;
  type: CodeType;
  statuses: Array<Timeline>;
  properties: {
    id: number;
    name: string;
    picture: string;
  }[];
  guest: {
    first_name: string;
    last_name: string;
    full_name: string;
  };
  first_used_at: string | null;
  device: MatchedDevice | null;
  reservation: {
    reservation_id: string;
    advanced_status: string;
  };
  computed?: {
    timeline?: VisualTimeline[];
  };
  thermostat_schedules?: {
    preset_name: string;
    cooling_set_point: number | null;
    heating_set_point: number | null;
    hvac_mode_setting: string;
    fan_mode_setting: string;
    manual_override_allowed: boolean;
  };
}

export type CodeActions =
  | 'delete'
  | 'edit'
  | 'edit_can_change_date'
  | 'edit_can_change_time'
  | 'can_be_ongoing'
  | 'edit_can_change_name'
  | 'can_attach_to_multiple_properties';

export type CodeType = 'backup' | 'manual' | 'reservation';

export const CodeTypeLabel: Record<CodeType, string> = {
  backup: 'Backup',
  manual: 'Manual',
  reservation: 'Reservation',
};

export interface Device {
  id: string;
  actions: string[];
  name: string;
  device_type: DeviceType;
  manufacturer: string | null;
  issues: Issue[];
  device_properties: DeviceProperties;
  min_length: number;
  max_length: number;
  integration: string; // integration_id uuid
  integration_key: IntegrationName;
  guest_access: boolean; // if false, the device is only used for operations (think: cleaners cupboard), not reservation codes
  computed?: {
    changingLockStatus?: boolean;
    property_ids?: number[];
  };
}

export interface MatchedDevice extends Device {
  properties: {
    id: number;
    name: string;
    picture: string;
  }[];
  code_length: number; // current length of the generated codes
}

export interface MatchedThermostat extends Device {
  properties: {
    id: number;
    name: string;
    picture: string;
  }[];
}

export interface UnmatchedDevice extends Device {
  unsupported_message: string;
}

export interface DeviceExtended extends MatchedDevice {
  codes_count: number; // The number of hospitable managed codes on this lock currently
  min_length: number; // device min code length
  max_length: number; // device max code length
}

export interface DeviceProperties {
  online: boolean | null;
  locked: boolean | null;
  battery: Battery | null;
  keypad: {
    battery: Battery | null;
    online: boolean | null;
  } | null;
  climate_setting: {
    mode: string | null;
    fan_mode: string | null;
    display_name: string | null;
    cooling_point: {
      fahrenheit: number | null;
      celsius: number | null;
    } | null;
    heating_point: {
      fahrenheit: number | null;
      celsius: number | null;
    } | null;
  } | null;
  available_fan_mode_settings: string[] | null;
  available_hvac_mode_settings: string[] | null;
  humidity: number | null;
  supported_temperatures: {
    max_cooling_set_point_celsius: number | null;
    max_cooling_set_point_fahrenheit: number | null;
    min_cooling_set_point_celsius: number | null;
    min_cooling_set_point_fahrenheit: number | null;
    max_heating_set_point_celsius: number | null;
    max_heating_set_point_fahrenheit: number | null;
    min_heating_set_point_celsius: number | null;
    min_heating_set_point_fahrenheit: number | null;
    min_heating_cooling_delta_celsius: number | null;
    min_heating_cooling_delta_fahrenheit: number | null;
  } | null;
}

export type DeviceType = 'lock' | 'thermostat';

export interface Issue {
  type: string;
  code: string;
  message: string;
}

export interface Battery {
  status: BatteryStatus;
  percentage: number | null;
  threshold: number; // 1-100 - the threshold at which we send notifications for low battery
}

export enum BatteryStatus {
  Full = 'full',
  Good = 'good',
  Low = 'low',
  Critical = 'critical',
  Unknown = 'unknown',
}

export interface Timeline {
  name: string;
  estimated_completion_at: string;
  is_latest: boolean;
}

export interface DeviceAccount {
  id: string; // uuid
  integration: IntegrationName;
  issues: Issue[];
  user_identifier: string; // email
  disconnected: boolean;
}

export interface SmartProperty {
  key: number; // same as id elsewhere
  timezone: string; // America/New York example
  label: string; // property name
  picture: string; // url
  code_length: number;
}

export type IntegrationName = 'yale' | 'august' | 'smartthings' | 'nuki' | 'igloohome' | 'schlage' | 'ttlock';

export interface VisualTimeline {
  title: string;
  subtitle?: string;
  cssClass?: string;
  isFilled: boolean;
}

export type ThermostatPresetType = 'occupied' | 'unoccupied' | 'long-term-unoccupied';

export interface ThermostatPreset {
  id: number;
  name: string;
  climate_preset_key: string;
  cooling_set_point_celsius: number;
  heating_set_point_celsius: number;
  cooling_set_point_fahrenheit: number;
  heating_set_point_fahrenheit: number;
  hvac_mode_setting: string;
  fan_mode_setting: string;
  manual_override_allowed: boolean;
  thermostats: MatchedThermostat[];
  is_default: boolean;
  type: ThermostatPresetType;
  timing: {
    unit: {
      value: number;
      value_append: 'h' | 'd' | 'm' | 's';
    };
    when: string;
  };
}

export const THERMOSTAT_TEMPERATURE_LIMITS = {
  MIN_FAHRENHEIT: 50,
  MAX_FAHRENHEIT: 90,
} as const;
